import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ClickAwayListener,
  Divider,
  InputAdornment,
  List,
  ListItem,
  Paper,
  Popper,
  TextField,
  Typography,
  Grid,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Fuse from "fuse.js";
import useDebounce from "../../../../hooks/useDebounce";
import { useBreakpoints } from "../../hooks/useBreakpoints";
import styled from "styled-components/macro";

const Result = styled(Typography)`
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const SearchResults = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  searchResults,
}) => {
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom"
      style={{ zIndex: 3 }}
      transition
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          style={{
            width: "80vw",
            maxWidth: "600px",
            height: "435px",
            maxHeight: "40vh",
            overflowY: "auto",
          }}
        >
          <List dense component="nav" aria-label="main mailbox folders">
            {searchResults?.slice(0, 49)?.map((result, i) => (
              <React.Fragment key={i}>
                <ListItem
                  style={{
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                  button
                  onClick={(e) => {
                    onSelect(result?.item);
                    onClose(e);
                  }}
                >
                  <Typography variant="subtitle1">
                    {result?.item?.well_id}
                  </Typography>
                  <Grid container>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption">Name</Typography>
                      <Result variant="body1">
                        {result?.item?.well_name || "N/A"}
                      </Result>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption">Subclass</Typography>
                      <Result variant="body1">
                        {result?.item?.well_subclass || "N/A"}
                      </Result>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption">Reference Name</Typography>
                      <Result variant="body1">
                        {result?.item?.well_reference_name || "N/A"}
                      </Result>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption">Address</Typography>
                      <Result variant="body1">
                        {result?.item?.well_address || "N/A"}
                      </Result>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption">Owner Info</Typography>
                      <Result variant="body1">
                        {result?.item?.owner_info || "N/A"}
                      </Result>
                    </Grid>
                  </Grid>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

const Search = ({ onSelect, data }) => {
  const searchRef = useRef(null);
  const { isXs } = useBreakpoints();

  const [value, setValue] = useState("");
  const debouncedSearchValue = useDebounce(value, 200);
  const [searchResults, setSearchResults] = useState([]);
  const [open, setOpen] = useState(false);

  const size = isXs ? "small" : "medium";

  useEffect(() => {
    setOpen(!!searchResults?.length);
  }, [searchResults]);

  const fuzzySearcher = useMemo(() => {
    if (data) {
      return new Fuse(data, {
        ignoreLocation: true,
        keys: [
          "well_id",
          "well_name",
          "well_reference_name",
          "well_address",
          "owner_name",
          "well_subclass",
        ],
      });
    }
  }, [data]);

  const handleClose = (event) => {
    if (searchRef.current && searchRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleChange = (event) => {
    setValue(event?.target?.value);
  };

  useEffect(() => {
    const results = fuzzySearcher && fuzzySearcher.search(debouncedSearchValue);
    setSearchResults(results);
  }, [debouncedSearchValue, fuzzySearcher]);

  return (
    <>
      <TextField
        id="outlined-basic"
        label="Barton Springs Well Search"
        autoComplete="off"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        onChange={handleChange}
        onFocus={() => !!value && setOpen(true)}
        placeholder="Well Attributes"
        ref={searchRef}
        style={{
          width: "100%",
          maxWidth: "300px",
          alignSelf: "center",
          marginTop: "8px",
        }}
        type="search"
        value={value}
        variant="outlined"
        size={size}
      />
      <SearchResults
        anchorEl={searchRef?.current}
        onClose={handleClose}
        onSelect={onSelect}
        open={open}
        searchResults={searchResults}
      />
    </>
  );
};

export default Search;
